import type { ReactNode } from 'react';
import { Stack, Group, SimpleGrid, Title, Text, Center, Loader, Divider } from '@mantine/core';
import { AreaChart, BarChart } from '@mantine/charts';
import { useCountry, SCOPE_LABEL } from '../lib/country';
import { useAsync, type AsyncState } from '../lib/useAsync';
import {
  fetchSummary,
  fetchOverview,
  fetchIraqOverview,
  fetchTimeseries,
  fetchIraqTimeseries,
  fetchRagOverview,
  fetchRagTimeseries,
  fetchRagTopQuestions,
  fetchCombinedUsers,
  type CombinedUsers,
  type AdminSummary,
  type OverviewStats,
  type RagOverview,
  type RagTopQuestions,
  type TimeseriesPoint,
  type RagTimeseriesPoint,
  type CountryScope,
} from '../lib/api';
import { StatCard } from '../components/StatCard';
import { SectionCard } from '../components/SectionCard';
import { RankBars } from '../components/RankBars';
import { fmtCompact, fmtPct, fmtDay, prettySubject } from '../lib/format';

const RAG_START = '2026-06-05'; // rag_request_log begins here; align the combined chart to it
const TODAY = new Date().toISOString().slice(0, 10);

// ── Section loaders (independent so the page paints immediately) ───────────────

async function loadChat(scope: CountryScope): Promise<TimeseriesPoint[]> {
  if (scope === 'jordan') return (await fetchTimeseries('day')).points;
  if (scope === 'iraq') return (await fetchIraqTimeseries('day')).points;
  const [j, i] = await Promise.all([fetchTimeseries('day'), fetchIraqTimeseries('day')]);
  return [...j.points, ...i.points];
}

async function loadVolume(scope: CountryScope): Promise<Array<{ date: string; 'Free-chat': number; 'Book/RAG': number }>> {
  const [chat, rag] = await Promise.all([loadChat(scope), fetchRagTimeseries('day', scope)]);
  const map = new Map<string, { day: string; chat: number; rag: number }>();
  const at = (d: string) => {
    let e = map.get(d);
    if (!e) {
      e = { day: d, chat: 0, rag: 0 };
      map.set(d, e);
    }
    return e;
  };
  for (const p of chat) at(p.bucket.slice(0, 10)).chat += p.userMessages;
  for (const p of rag.points) at(p.bucket.slice(0, 10)).rag += p.count;
  return [...map.values()]
    .filter((d) => d.day >= RAG_START && d.day < TODAY) // start at Jun 5, drop the partial current day
    .sort((a, b) => a.day.localeCompare(b.day))
    .map((d) => ({ date: fmtDay(d.day), 'Free-chat': d.chat, 'Book/RAG': d.rag }));
}

function Async<T>({ state, h = 220, children }: { state: AsyncState<T>; h?: number; children: (d: T) => ReactNode }) {
  if (state.loading && !state.data)
    return (
      <Center h={h}>
        <Loader color="brand" size="sm" />
      </Center>
    );
  if (state.error && !state.data)
    return (
      <Center h={h}>
        <Text size="sm" c="red.7" ff="monospace">
          {state.error}
        </Text>
      </Center>
    );
  if (!state.data) return null;
  return <>{children(state.data)}</>;
}

export function Dashboard() {
  const { scope } = useCountry();
  const summary = useAsync<AdminSummary>(() => fetchSummary(), [scope], 60_000);
  const volume = useAsync(() => loadVolume(scope), [scope], 120_000);
  const rag = useAsync<RagOverview>(() => fetchRagOverview(scope), [scope], 120_000);
  const top = useAsync<RagTopQuestions>(() => fetchRagTopQuestions(scope, 12), [scope], 300_000);
  const eng = useAsync<OverviewStats>(() => (scope === 'iraq' ? fetchIraqOverview() : fetchOverview()), [scope], 120_000);
  const users = useAsync<CombinedUsers>(() => fetchCombinedUsers(), [], 300_000);

  return (
    <Stack gap="lg">
      <div>
        <Title order={2} c="navy.9" fw={600}>
          {SCOPE_LABEL[scope]} usage
        </Title>
        <Text c="dimmed" size="sm" mt={2}>
          Free-chat and book questions, combined
        </Text>
      </div>

      {/* Hero */}
      <Async state={summary} h={140}>
        {(d) => {
          const s = d[scope];
          const ragShare = s.total > 0 ? s.rag / s.total : 0;
          return (
            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="lg">
              <StatCard label="Free-chat" value={s.chat} sub="messages typed in chat" />
              <StatCard label="Book questions" value={s.rag} sub="asked while reading" />
              <StatCard label="Total questions" value={s.total} variant="filled" sub={`${fmtPct(ragShare)} are book questions`} />
            </SimpleGrid>
          );
        }}
      </Async>

      {/* Daily volume */}
      <SectionCard title="Daily volume" hint="Free-chat vs book questions, since 5 Jun (current day excluded)">
        <Async state={volume} h={300}>
          {(data) => (
            <AreaChart
              h={320}
              data={data}
              dataKey="date"
              series={[
                { name: 'Free-chat', color: 'gray.5' },
                { name: 'Book/RAG', color: 'brand.6' },
              ]}
              curveType="monotone"
              withDots={false}
              withLegend
              valueFormatter={fmtCompact}
              gridAxis="x"
            />
          )}
        </Async>
      </SectionCard>

      {/* Book / RAG */}
      <Divider label="Book questions" labelPosition="left" />
      <Async state={rag} h={300}>
        {(ov) => {
          const subjects = ov.snapshot.bySubject.slice(0, 10).map((x) => ({ label: prettySubject(x.collectionId), value: x.count }));
          const gradeLabel = (g: string) =>
            g === 'grade-10' ? 'Grade 10' : g === 'grade-11' ? 'Grade 11' : g === 'grade-12' ? 'Grade 12' : 'Unknown';
          const grades = ov.snapshot.byGrade.map((g) => ({
            grade: gradeLabel(g.grade),
            Questions: g.count,
            'Unique users': g.uniqueUsers,
          }));
          return (
            <Stack gap="lg">
              <SectionCard
                title="Usage by grade"
                hint={`book questions · ${SCOPE_LABEL[scope]} · counts every logged request, IPs deduped per grade`}
              >
                {grades.length ? (
                  <BarChart
                    h={260}
                    data={grades}
                    dataKey="grade"
                    series={[
                      { name: 'Questions', color: 'brand.6' },
                      { name: 'Unique users', color: 'navy.6' },
                    ]}
                    withLegend
                    valueFormatter={fmtCompact}
                    gridAxis="y"
                  />
                ) : (
                  <Text size="sm" c="dimmed" ta="center" py="md">No grade data</Text>
                )}
              </SectionCard>
              <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
                <SectionCard title="Most-asked books">
                  <RankBars items={subjects} color="brand.6" />
                </SectionCard>
                <SectionCard title="Top questions" hint="asked repeatedly">
                  <Async state={top} h={160}>
                    {(t) => <RankBars items={t.questions.map((q) => ({ label: q.question, value: q.count }))} color="brand.5" rtl emptyLabel="Aggregating" />}
                  </Async>
                </SectionCard>
              </SimpleGrid>
            </Stack>
          );
        }}
      </Async>

      {/* Free-chat engagement */}
      <Divider label={`Free-chat engagement${scope === 'all' ? ' · Jordan' : ''}`} labelPosition="left" />
      <Async state={eng} h={300}>
        {(o) => {
          const e = o.engagement;
          const combined = users.data ? users.data[scope] : o.totals.users;
          const returning = users.data?.returning?.[scope];
          const depth = e.sessionDepth.map((b) => ({ bucket: b.bucket, Sessions: b.count }));
          const durations = e.sessionDurations.map((b) => ({ bucket: b.bucket, Sessions: b.count }));
          const newUsers = e.newUsersByDay
            .filter((d) => d.date.slice(0, 10) < TODAY) // drop the partial current day so the tail never fakes a decline
            .sort((a, b) => a.date.localeCompare(b.date))
            .map((d) => ({ date: fmtDay(d.date), 'New users': d.count }));
          return (
            <Stack gap="lg">
              <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="lg">
                <StatCard label="Users" value={combined} sub="distinct IPs" />
                <StatCard
                  label="Returning users"
                  value={returning ?? '—'}
                  variant="filled"
                  sub={returning !== undefined && combined > 0 ? `${fmtPct(returning / combined)} came back on 2+ days` : 'came back on 2+ days'}
                />
                <StatCard label="Sessions" value={o.totals.sessions} />
                <StatCard label="Messages / user" value={e.avgMessagesPerUser} />
                <StatCard label="Messages / session" value={e.avgMessagesPerSession} />
              </SimpleGrid>
              <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
                <SectionCard title="Session depth" hint="sessions by message count">
                  <BarChart h={240} data={depth} dataKey="bucket" series={[{ name: 'Sessions', color: 'brand.6' }]} withLegend={false} valueFormatter={fmtCompact} gridAxis="y" />
                </SectionCard>
                <SectionCard title="Session length" hint="first to last message">
                  <BarChart h={240} data={durations} dataKey="bucket" series={[{ name: 'Sessions', color: 'navy.6' }]} withLegend={false} valueFormatter={fmtCompact} gridAxis="y" />
                </SectionCard>
              </SimpleGrid>
              <SectionCard title="New users per day" hint="first-seen IPs, last 30 days">
                <AreaChart h={220} data={newUsers} dataKey="date" series={[{ name: 'New users', color: 'brand.6' }]} curveType="monotone" withDots={false} withLegend={false} valueFormatter={fmtCompact} gridAxis="x" />
              </SectionCard>
            </Stack>
          );
        }}
      </Async>
    </Stack>
  );
}
